import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// Form Elements
import { Heading, Submit } from "../FormElements";
import { firstStepValidator } from "../Validation/FirstStepValidator";

// Helpers
import { interpolate as inter, isObjectEmpty } from "../Helpers";
import { FieldStatus } from "../Helpers/FieldStatus";
import { RootDataType } from "../Helpers/interfaces";
import { clear, onSubmit, setStorage } from "../Helpers/formFunctions";

// Types
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../Helpers/formTypes";
import { useLabels } from "../Hooks/useLabels";
import { ServiceOrGroupSelect } from "../FormElements/Select/ServiceOrGroupSelect";
import { LocationSelect } from "../FormElements/Select/LocationSelect";
import { SpecializationSelect } from "../FormElements/Select/SpecializationSelect";
import { useFilteredSpecializations } from "../Hooks/useFilteredSpecializations";
import { useFilteredServices } from "../Hooks/useFilteredServices";
import { useFilteredLocations } from "../Hooks/useFilteredLocations";
import { LocationDto } from "@apiModels/LocationDto.csharp";
import { GtmService } from "../GtmService";
import { SpecializationDto } from "@apiModels/SpecializationDto.csharp";
import { ServiceDto } from "@apiModels/ServiceDto.csharp";
import { ServiceGroupDto } from "@apiModels/ServiceGroupDto.csharp";
import { useRelatedServiceSetups } from "../Hooks/useRelatedServiceSetups";
import { DoctorDto } from "../../../../AdminPortal/Features/Models/DoctorDto.csharp";
import { useMissingAppointmentsMessage } from "../Hooks/useNoAppointmentsMessage";
import { useMissingAppointments } from "../Hooks/useMissingAppointments";
import { RichTextArea } from "../RichTextArea/RichTextArea";

const fields = {
  specialization: {
    label: "Velg tjeneste",
    name: FormFields.specialization,
  },
  service: {
    label: "Velg behandling",
    name: FormFields.service,
  },
  serviceGroup: {
    label: "Velg behandling",
    name: FormFields.serviceGroup,
  },
  location: {
    label: "Velg sted",
    name: FormFields.location,
  },
  doctor: {
    name: FormFields.doctor,
  },
};

export type FirstStepType = {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
};

export const FirstStep: FC<FirstStepType> = ({
  state,
  dispatch,
  nextStep,
  rootData,
}) => {
  const { bookingLabels } = useLabels();
  const firstStepLabels = bookingLabels!.stepsBookingLabels.firstStepLabels;
  const commonLabels = bookingLabels!.commonLabels;
  const errorLabels = bookingLabels!.bookingErrors;

  useRelatedServiceSetups(state.service, dispatch);

  useMissingAppointments(state.service, state.showRelatedServicesMap, dispatch);

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<any>({
    defaultValues: state,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(firstStepValidator(errorLabels)),
  });

  const { filteredSpecializations, unavailableSpecializations } =
    useFilteredSpecializations(
      rootData.specializations,
      rootData.services,
      state.location,
      state.doctor
    );

  const {
    filteredServices,
    unavailableServices,
    filteredGroupServices,
    unavailableGroupServices,
  } = useFilteredServices(
    rootData.services,
    rootData.serviceGroups,
    state.specialization,
    state.doctor,
    state.location,
    state.serviceGroup
  );

  const { filteredLocations, unavailableLocations } = useFilteredLocations(
    rootData.centers,
    state.specialization,
    state.service,
    state.serviceGroup,
    state.doctor.id && state.doctor.id != "-1" ? state.doctor : undefined
  );

  const { missingAppointmentsMessage } = useMissingAppointmentsMessage(
    state.doctor,
    state.location,
    state.specialization,
    state.service,
    state.missingAppointments,
    rootData.centers
  );

  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (initializing) {
      const preselectedLocation =
        state.location.id &&
        state.location.id != "-1" &&
        state.location.isLocation;
      if (preselectedLocation) {
        const centers = filteredLocations.filter((l) =>
          state.location.centers.includes(l.id)
        );
        if (centers.length == 1) {
          onLocationSelect(centers[0]);
          return;
        }
      }
      const locations = filteredLocations.filter(
        (l) => l.isLocation && l.id != "-1"
      );
      if (locations.length == 1) {
        const centers = filteredLocations.filter((l) => l.isLocation === false);
        if (centers.length == 1) {
          onLocationSelect(centers[0]);
        } else {
          onLocationSelect(locations[0]);
        }
      }
    }
  }, [filteredLocations]);

  const triggerValidator = () => {
    if (state.specialization.id) {
      trigger(fields.specialization.name).then(null);
    }

    if (state.service.id) {
      trigger(fields.service.name).then(null);
      trigger("serviceSecondLevel").then(null);
    } else if (state.serviceGroup.id) {
      trigger(fields.service.name).then(null);
    }

    if (state.location.id) {
      trigger(fields.location.name).then(null);
    }
  };

  useEffect(() => {
    if (
      !isObjectEmpty(state.location) ||
      !isObjectEmpty(state.service) ||
      !isObjectEmpty(state.specialization)
    ) {
      setStorage(state);
    }

    triggerValidator();
  }, [state]);

  function firstStepSubmit() {
    GtmService.bookingSubmitFirstStep(
      state.specialization?.name,
      state.specialization?.id,
      state.service?.bookingName,
      state.service?.id,
      state.location?.name,
      state.location?.id
    );
    return onSubmit(nextStep, errors);
  }

  const onLocationSelect = (location: LocationDto) => {
    setValue(fields.location.name, location);
    dispatch({
      type: FormReducer.update,
      field: FormFields.location,
      payload: location,
    });
  };

  const onDoctorSelect = (doctor: DoctorDto) => {
    setValue(fields.doctor.name, doctor);
    dispatch({
      type: FormReducer.update,
      field: FormFields.doctor,
      payload: doctor,
    });
  };

  function onSpecializationSelect(
    specialization: SpecializationDto,
    userInput = true,
    clearServicesInput = true
  ) {
    setInitializing(!userInput);
    if (clearServicesInput) {
      clear(FormFields.service, dispatch, setValue);
      clear(FormFields.serviceGroup, dispatch, setValue);
    }

    setValue(fields.specialization.name, specialization);
    dispatch({
      type: FormReducer.update,
      field: FormFields.specialization,
      payload: specialization,
    });
  }

  function onServiceOrGroupSelect(
    serviceOrGroup: ServiceDto | ServiceGroupDto
  ) {
    const name = serviceOrGroup.isGroup
      ? fields.serviceGroup.name
      : fields.service.name;
    if (serviceOrGroup.isGroup) {
      clear(FormFields.service, dispatch, setValue);
    } else {
      clear(FormFields.serviceGroup, dispatch, setValue);
    }

    setValue(name, serviceOrGroup);
    dispatch({
      type: FormReducer.update,
      field: name,
      payload: serviceOrGroup,
    });
  }

  function onServiceSelect(serviceOrGroup: ServiceDto | ServiceGroupDto) {
    setValue(fields.service.name, serviceOrGroup);
    dispatch({
      type: FormReducer.update,
      field: FormFields.service,
      payload: serviceOrGroup,
    });
  }

  return (
    <div className="mb-3">
      <Heading>
        {state.doctor?.id && state.doctor.id != "-1"
          ? inter(
              firstStepLabels?.subHeadingDoctors!,
              "$doctor",
              state.doctor.name
            )
          : firstStepLabels?.subHeading}
      </Heading>
      <form
        className="d-flex flex-column"
        onSubmit={handleSubmit(firstStepSubmit)}
      >
        <SpecializationSelect
          defaultValue={state.specialization}
          onSelect={onSpecializationSelect}
          label={firstStepLabels?.specializationDropdownLabel}
          placeholder={firstStepLabels?.specializationDropdownPlaceholder}
          name={fields.specialization.name}
          specializations={{
            items: filteredSpecializations,
          }}
          unavailableSpecializations={{ items: unavailableSpecializations }}
          location={state.location}
          doctor={state.doctor}
          additionalLabels={firstStepLabels}
          error={errors[fields.specialization.name]}
          status={
            !state.service
              ? FieldStatus.disabled
              : errors[fields.specialization.name]
              ? FieldStatus.error
              : FieldStatus.active
          }
        />
        <ServiceOrGroupSelect
          defaultValue={
            state.serviceGroup?.id ? state.serviceGroup : state.service
          }
          onSelect={onServiceOrGroupSelect}
          label={firstStepLabels?.serviceDropdownLabel}
          placeholder={firstStepLabels?.serviceDropdownPlaceholder}
          services={{ items: filteredServices }}
          unavailableServices={{ items: unavailableServices }}
          dependencies={[state.specialization.id]}
          location={state.location}
          doctor={state.doctor}
          additionalLabels={firstStepLabels}
          error={errors[fields.service.name]}
          status={
            errors[fields.service.name]
              ? FieldStatus.error
              : state.specialization.id
              ? FieldStatus.active
              : FieldStatus.disabled
          }
        />

        {!!state.serviceGroup?.services?.length && (
          <ServiceOrGroupSelect
            defaultValue={state.service}
            onSelect={onServiceSelect}
            label={
              state.serviceGroup?.serviceFromGroupDropdownLabel ??
              firstStepLabels?.serviceFromGroupDropdownLabel
            }
            placeholder={
              state.serviceGroup?.serviceFromGroupDropdownPlaceholder ??
              firstStepLabels?.serviceFromGroupDropdownPlaceholder
            }
            services={{ items: filteredGroupServices }}
            unavailableServices={{ items: unavailableGroupServices }}
            dependencies={[state.specialization.id]}
            location={state.location}
            doctor={state.doctor}
            additionalLabels={firstStepLabels}
            error={errors["serviceSecondLevel"]}
            status={
              errors["serviceSecondLevel"]
                ? FieldStatus.error
                : FieldStatus.active
            }
          />
        )}

        <LocationSelect
          defaultValue={state.location}
          onSelect={onLocationSelect}
          label={commonLabels.placeDropdownLabel}
          placeholder={commonLabels.placeDropdownPlaceholder}
          locations={{
            items: filteredLocations,
          }}
          unavailableLocations={{ items: unavailableLocations }}
          dependencies={[state.specialization.id, state.service.id]}
          error={errors[fields.location.name]}
          service={state.service}
          specialization={state.specialization}
          doctor={state.doctor}
          onDoctorSelect={onDoctorSelect}
          additionalLabels={commonLabels}
          status={
            errors[fields.location.name]
              ? FieldStatus.error
              : FieldStatus.active
          }
        />
        
        {!missingAppointmentsMessage ? (
          <Submit label={firstStepLabels?.buttonLabel} />
        ) : (
          <RichTextArea
            content={missingAppointmentsMessage}
            innerClassName={"booking-narrow-item mt-2"}
          />
        )}
      </form>
    </div>
  );
};
